import React, { useState, useMemo } from 'react';
import {
  Popover,
  Box,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  ButtonGroup,
  Divider,
  IconButton,
  Switch,
  InputAdornment,
  Chip,
  useTheme,
} from '@mui/material';
import {
  DragIndicator as DragIcon,
  Search as SearchIcon,
  RestartAlt as ResetIcon,
  PushPin as PinIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useLanguage } from '../context/LanguageContext';

function SortableColumnItem({ id, label, checked, onToggle }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        px: 1,
        py: 0.25,
        borderRadius: 1,
        '&:hover': { bgcolor: 'action.hover' },
        cursor: 'default',
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        sx={{
          cursor: 'grab',
          display: 'flex',
          alignItems: 'center',
          color: 'text.disabled',
          mr: 0.5,
          '&:active': { cursor: 'grabbing' },
        }}
      >
        <DragIcon fontSize="small" />
      </Box>
      <FormControlLabel
        sx={{ flex: 1, m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.85rem' } }}
        control={
          <Checkbox
            size="small"
            checked={checked}
            onChange={() => onToggle(id)}
          />
        }
        label={label}
      />
    </Box>
  );
}

export default function ColumnSettingsPanel({
  anchorEl,
  open,
  onClose,
  settings,
  allColumns,
  systemColumns,
  onToggleColumn,
  onReorderColumns,
  onApplyPreset,
  onResetToDefault,
  onTogglePinFirstColumn,
  translateColumn,
}) {
  const theme = useTheme();
  const { t } = useLanguage();
  const [search, setSearch] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const userColumns = useMemo(
    () => (settings?.columnOrder || allColumns).filter((c) => !systemColumns.includes(c)),
    [settings, allColumns, systemColumns]
  );

  const filteredColumns = useMemo(() => {
    if (!search.trim()) return userColumns;
    const q = search.toLowerCase();
    return userColumns.filter(
      (c) =>
        c.toLowerCase().includes(q) ||
        (translateColumn(c) || '').toLowerCase().includes(q)
    );
  }, [userColumns, search, translateColumn]);

  const visibleSet = useMemo(
    () => new Set(settings?.visibleColumns || []),
    [settings]
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = userColumns.indexOf(active.id);
    const newIndex = userColumns.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = [...userColumns];
    newOrder.splice(oldIndex, 1);
    newOrder.splice(newIndex, 0, active.id);
    onReorderColumns(newOrder);
  };

  if (!settings) return null;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          sx: { width: 340, maxHeight: '70vh', display: 'flex', flexDirection: 'column' },
        },
      }}
    >
      <Box sx={{ p: 2, pb: 1 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
          Column Settings
        </Typography>

        {/* Presets */}
        <ButtonGroup size="small" fullWidth sx={{ mb: 1.5 }}>
          {[
            { id: 'compact', label: 'Compact' },
            { id: 'contacts', label: 'Contacts' },
            { id: 'full', label: 'Full' },
          ].map((p) => (
            <Button
              key={p.id}
              variant={settings.preset === p.id ? 'contained' : 'outlined'}
              onClick={() => onApplyPreset(p.id)}
              sx={{ textTransform: 'none', fontSize: '0.8rem' }}
            >
              {p.label}
            </Button>
          ))}
        </ButtonGroup>

        {/* Pin toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <PinIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
          <FormControlLabel
            sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.85rem' } }}
            control={
              <Switch
                size="small"
                checked={settings.pinFirstColumn !== false}
                onChange={onTogglePinFirstColumn}
              />
            }
            label="Pin company name column"
          />
        </Box>

        <Divider sx={{ mb: 1 }} />

        {/* Search */}
        <TextField
          size="small"
          fullWidth
          placeholder="Search columns..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1 }}
        />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 0.5,
          }}
        >
          <Chip
            size="small"
            label={`${visibleSet.size} / ${userColumns.length}`}
            color="primary"
            variant="outlined"
          />
          <Button
            size="small"
            startIcon={<ResetIcon fontSize="small" />}
            onClick={onResetToDefault}
            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
          >
            Reset
          </Button>
        </Box>
      </Box>

      {/* Scrollable column list */}
      <Box sx={{ overflowY: 'auto', flex: 1, px: 1, pb: 1 }}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={filteredColumns}
            strategy={verticalListSortingStrategy}
          >
            {filteredColumns.map((col) => (
              <SortableColumnItem
                key={col}
                id={col}
                label={translateColumn(col) || col}
                checked={visibleSet.has(col)}
                onToggle={onToggleColumn}
              />
            ))}
          </SortableContext>
        </DndContext>

        {filteredColumns.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: 'center', py: 2 }}
          >
            No columns match your search
          </Typography>
        )}
      </Box>
    </Popover>
  );
}
